/**
 * Extracts a user-facing error message from an API/axios error.
 * Handles plain string, { message }, and ASP.NET Core ValidationProblemDetails (errors).
 * Use this so users see useful messages instead of generic "Failed to save...".
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error == null || typeof error !== 'object' || !('response' in error)) {
    return fallback;
  }

  const axiosError = error as {
    response?: {
      data?: string | Record<string, unknown>;
      status?: number;
    };
  };
  const data = axiosError.response?.data;

  if (data == null) {
    return fallback;
  }

  // Backend returned a plain string
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  if (typeof data !== 'object') {
    return fallback;
  }

  // Explicit message (e.g. BadRequest(new { message = "..." }))
  if (typeof (data as { message?: string }).message === 'string') {
    const msg = (data as { message?: string }).message!.trim();
    if (msg.length > 0) return msg;
  }

  // Legacy/alternate error field
  if (typeof (data as { error?: string }).error === 'string') {
    const errMsg = (data as { error?: string }).error!.trim();
    if (errMsg.length > 0) return errMsg;
  }

  // ASP.NET Core ValidationProblemDetails: errors = { "FieldName": ["error1", "error2"] }
  const errors = (data as { errors?: Record<string, string[] | undefined> }).errors;
  if (errors != null && typeof errors === 'object') {
    const firstMessages: string[] = [];
    for (const key of Object.keys(errors)) {
      const arr = errors[key];
      if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'string') {
        firstMessages.push(arr[0].trim());
      }
    }
    if (firstMessages.length > 0) {
      return firstMessages.length === 1
        ? firstMessages[0]
        : firstMessages.join('. ');
    }
  }

  // ProblemDetails detail or title
  const detail = (data as { detail?: string }).detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail.trim();
  }
  const title = (data as { title?: string }).title;
  if (typeof title === 'string' && title.trim().length > 0) {
    return title.trim();
  }

  return fallback;
}
